import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import { Button, Checkbox, Form, Modal } from 'semantic-ui-react';

import { useDidUpdate, useToggle } from '../../lib/hooks';
import { Input } from '../../lib/custom-ui';
import { utcToLocalDatePicker, localDatePickerToUtc, formatMoscowTime, parseMoscowTime } from '../../utils/moscowTime';
import { useForm } from '../../hooks';

import styles from './CardReminderStep.module.scss';

const CardReminderStep = React.memo(({ defaultValue, defaultEnabled, onApply, onClose }) => {
  const [t] = useTranslation();
  const datePickerDateFormat = t('format:datePickerDate', { defaultValue: 'yyyy.MM.dd' });
  const datePickerDateTimeFormat = t('format:datePickerDateTime', {
    defaultValue: 'yyyy.MM.dd HH:mm',
  });

  const [enabled, setEnabled] = useState(Boolean(defaultEnabled));

  const [data, handleFieldChange, setData] = useForm(() => {
    // defaultValue приходит с сервера в UTC
    let date = defaultValue;
    if (!date) {
      // Если даты нет, создаем текущую дату в МСК
      const now = new Date();
      date = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        12, 0, 0, 0
      ));
    }
    // Форматируем в МСК времени для отображения
    return {
      date: formatMoscowTime(date, datePickerDateFormat),
      time: formatMoscowTime(date, 'HH:mm'),
    };
  });

  const handleFieldChangeAndEnable = useCallback(
    (event, field) => {
      handleFieldChange(event, field);
      setEnabled(true);
    },
    [handleFieldChange],
  );

  const [selectTimeFieldState, selectTimeField] = useToggle();

  const dateField = useRef(null);
  const timeField = useRef(null);

  const nullableDate = useMemo(() => {
    // Парсим дату как МСК время
    const dateStr = `${data.date} ${data.time || '00:00'}`;
    const utcDate = parseMoscowTime(dateStr);
    
    if (utcDate && !Number.isNaN(utcDate.getTime())) {
      // Конвертируем UTC в дату для datePicker (показывать как МСК)
      return utcToLocalDatePicker(utcDate);
    }

    return null;
  }, [data.date, data.time]);

  const handleDatePickerChange = useCallback(
    (date) => {
      // date приходит из react-datepicker (локальное время браузера)
      // Конвертируем в МСК время, затем в UTC для отправки на сервер
      const utcDate = localDatePickerToUtc(date);
      setData((prevData) => ({
        ...prevData,
        date: formatMoscowTime(utcDate, datePickerDateFormat),
      }));
      setEnabled(true);
      selectTimeField();
    },
    [setData, selectTimeField, datePickerDateFormat],
  );

  const handleSubmit = useCallback(() => {
    if (!enabled) {
      onApply({
        date: defaultValue || null,
        enabled: false,
      });
      onClose();
      return;
    }

    if (!nullableDate) {
      dateField.current.select();
      return;
    }

    // Парсим дату и время как МСК время, конвертируем в UTC для отправки на сервер
    const dateStr = `${data.date} ${data.time}`;
    const value = parseMoscowTime(dateStr);

    if (!value || Number.isNaN(value.getTime())) {
      timeField.current.select();
      return;
    }

    // Отправляем UTC дату на сервер
    onApply({
      date: value,
      enabled: true,
    });
    onClose();
  }, [enabled, defaultValue, nullableDate, onApply, onClose, data]);

  const handleClearClick = useCallback(() => {
    onApply({
      date: null,
      enabled: false,
    });
    onClose();
  }, [onApply, onClose]);

  useEffect(() => {
    dateField.current.select();
  }, []);

  useDidUpdate(() => {
    if (timeField.current) {
      timeField.current.select();
    }
  }, [selectTimeFieldState]);

  return (
    <>
      <Modal.Header>Напоминание</Modal.Header>
      <Modal.Content>
        <div className={styles.controlsRow}>
          <Checkbox
            checked={enabled}
            label="Активировать"
            onChange={() => setEnabled((prev) => !prev)}
          />
        </div>
        <Form onSubmit={handleSubmit}>
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.date')} (МСК)</div>
              <Input
                ref={dateField}
                name="date"
                value={data.date}
                onChange={handleFieldChangeAndEnable}
              />
            </div>
            <div className={styles.fieldBox}>
              <div className={styles.text}>{t('common.time')} (МСК)</div>
              <InputMask
                mask="99:99"
                placeholder="__:__"
                value={data.time}
                onChange={(e) =>
                  handleFieldChangeAndEnable(null, { name: 'time', value: e.target.value })
                }
              >
                {(inputProps) => <Input {...inputProps} />}
              </InputMask>
            </div>
          </div>
          <DatePicker
            inline
            disabledKeyboardNavigation
            selected={nullableDate}
            onChange={handleDatePickerChange}
          />
        </Form>
        <div className={styles.buttonsRow}>
          <Button type="button" positive content="Применить" onClick={handleSubmit} />
          <Button type="button" negative content="Удалить" onClick={handleClearClick} />
        </div>
      </Modal.Content>
    </>
  );
});

CardReminderStep.propTypes = {
  defaultValue: PropTypes.instanceOf(Date),
  defaultEnabled: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

CardReminderStep.defaultProps = {
  defaultValue: undefined,
  defaultEnabled: false,
};

export default CardReminderStep;

